import { type FC, type ReactNode } from 'react';
import { graphql, useFragment } from 'react-relay';
import classnames from 'classnames';

import dibsCss from 'dibs-css';
import { getSrcsetUrl } from 'dibs-image-utils/exports/srcSet.ts';

import { type PdpSharedSellerBrandingInfo_seller$key } from './__generated__/PdpSharedSellerBrandingInfo_seller.graphql.ts';

import styles from './PdpSharedSellerBrandingInfo.module.css';

const sellerFragment = graphql`
    fragment PdpSharedSellerBrandingInfo_seller on Seller {
        liveStorefrontProfile {
            photos(photoType: DEALER_STOREFRONT_HEADSHOT) {
                status
                path
            }
        }
        sellerProfile {
            company
        }
    }
`;

type Props = {
    seller: PdpSharedSellerBrandingInfo_seller$key | null | undefined;
    showSellerBranding?: boolean;
    wrapperClassName?: string;
    imageClassName?: string;
    titleClassName?: string;
    noImageTitleClassName?: string;
    children?: ReactNode;
};

export const PdpSharedSellerBrandingInfo: FC<Props> = ({
    seller: sellerRef,
    showSellerBranding,
    wrapperClassName,
    imageClassName,
    titleClassName,
    noImageTitleClassName,
    children,
}) => {
    const seller = useFragment(sellerFragment, sellerRef);

    if (!showSellerBranding) {
        return children;
    }

    const { liveStorefrontProfile, sellerProfile } = seller || {};
    const photo = liveStorefrontProfile?.photos?.[0];

    const sellerImagePath = photo?.status === 'ACTIVE' ? photo.path : null;
    const company = sellerProfile?.company;

    return (
        <div
            className={classnames(
                dibsCss.flex,
                dibsCss.itemsStart,
                dibsCss.gapXsmall,
                wrapperClassName
            )}
        >
            {sellerImagePath && (
                <img
                    alt={company || ''}
                    src={getSrcsetUrl(sellerImagePath, 150)}
                    className={classnames(dibsCss.roundedFull, imageClassName, {
                        [styles.sellerImage]: !imageClassName,
                    })}
                />
            )}
            <div className={classnames(dibsCss.flex, dibsCss.flexCol, dibsCss.flex1)}>
                {company && (
                    <div
                        className={classnames(
                            dibsCss.sassyFontBodyMediumHeavy,
                            dibsCss.mbXxsmall,
                            titleClassName,
                            !sellerImagePath && noImageTitleClassName
                        )}
                    >
                        {company}
                    </div>
                )}
                {children}
            </div>
        </div>
    );
};
